import { useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";

import { useMutation } from "@apollo/client";

import { type AbsoluteRoute, type RouteParams } from "../../core/routes";
import { useLensManagementRoutes } from "../../core/routes/hooks";
import { type ApolloResult } from "../../shared/models";
import { type DeleteLensMutation, type DeleteLensMutationVariables, deleteLensMutation } from "../api/mutations";

export const useDeleteLens = (
    lensId: string,
): ApolloResult<{
    deleteLens: () => Promise<void>;
}> => {
    const { orgId, lensFolderId } = useParams<RouteParams[AbsoluteRoute.ORG_LENS_FOLDER_LENS]>();
    const { goToLensesList, goToLensesListForLensFolder } = useLensManagementRoutes();

    const redirectToLensesList = useMemo(() => {
        if (orgId && lensFolderId) {
            return () => {
                goToLensesListForLensFolder(orgId, lensFolderId);
            };
        }
        return goToLensesList;
    }, [orgId, lensFolderId, goToLensesListForLensFolder, goToLensesList]);

    const [deleteLensInfo, { loading, error }] = useMutation<DeleteLensMutation, DeleteLensMutationVariables>(
        deleteLensMutation,
    );

    const deleteLens = useCallback(
        () =>
            deleteLensInfo({
                variables: {
                    lensId,
                },
                refetchQueries: () => {
                    return ["getLensesList"];
                },
            }).then(redirectToLensesList),

        [lensId, deleteLensInfo, redirectToLensesList],
    );

    return { deleteLens, loading, error };
};
